/**
 * UsageIndicator Component
 * 
 * Displays calculation usage count to authenticated users.
 * Shows remaining calculations with color-coded status.
 */

import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import styles from './UsageIndicator.module.css';

/**
 * UsageIndicator component props
 */
export interface UsageIndicatorProps {
  /** Current calculation count */
  usageCount: number;
  /** Usage limit (always 5) */
  usageLimit: number;
  /** Whether usage data is being loaded */
  isLoading: boolean;
}

/**
 * UsageIndicator Component
 * 
 * Displays usage count as a badge with color-coded status:
 * - Normal: >= 2 remaining (neutral)
 * - Warning: 1 remaining (yellow/orange)
 * - Limit Reached: 0 remaining (red)
 */
export const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  usageCount,
  usageLimit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const remaining = Math.max(0, usageLimit - usageCount);

  // Determine status class for styling
  let statusClass = styles.normal;
  if (remaining === 0) {
    statusClass = styles.limitReached;
  } else if (remaining === 1) {
    statusClass = styles.warning;
  }

  // Build display text
  let displayText: string;
  if (isLoading) {
    displayText = t.usageIndicator.loading;
  } else {
    displayText = t.usageIndicator.template.replace('{count}', remaining.toString());
  }

  return (
    <div className={`${styles.badge} ${statusClass}`} title={displayText}>
      <span className={styles.badgeText}>{displayText}</span>
    </div>
  );
};

